import React, { useState, useMemo } from 'react';
import { Dropdown, Label, SemanticCOLORS, Pagination, Button, Popup, Icon } from 'semantic-ui-react';
import { Table, HeaderCell, TableBody, TableCell, TableHeader, TableRow } from 'common/components/Table';
import { PageSection } from 'common/styles/page';
import { MetricList as MetricListType } from '../api/metricsApi';
import { AssessmentPeriodMetricList } from '../api/assessmentPeriodMetricsApi';
import { getMetricTypeDisplay, MetricType } from './MetricTypeSelector';
import LoadingSpinner from 'common/components/LoadingSpinner';

type TabType = 'definitions' | 'values';

// Filter option types
export interface FilterOption {
  key: string | number;
  text: string;
  value: any;
}

// Filter configuration for values tab
export interface ValuesFilterConfig {
  organizationOptions: FilterOption[];
  assessmentPeriodOptions: FilterOption[];
  teamOptions: FilterOption[];
  metricOptions: FilterOption[];
  organizationValue: number | undefined;
  assessmentPeriodValue: number | undefined;
  teamValue: number | null | undefined;
  metricValue: number | undefined;
  onOrganizationChange: (value: number | undefined) => void;
  onAssessmentPeriodChange: (value: number | undefined) => void;
  onTeamChange: (value: number | null | undefined) => void;
  onMetricChange: (value: number | undefined) => void;
  onClearFilters: () => void;
  isOrganizationDisabled?: boolean;
  isAssessmentPeriodDisabled?: boolean;
  isTeamDisabled?: boolean;
  isMetricDisabled?: boolean;
  isLoadingOptions?: boolean;
}

// Filter configuration for definitions tab
export interface DefinitionsFilterConfig {
  metricTypeOptions: FilterOption[];
  statusOptions: FilterOption[];
  metricTypeValue: number | undefined;
  statusValue: boolean | undefined;
  onMetricTypeChange: (value: number | undefined) => void;
  onStatusChange: (value: boolean | undefined) => void;
  onClearFilters: () => void;
}

interface MetricListProps {
  metrics: MetricListType[] | AssessmentPeriodMetricList[];
  isLoading: boolean;
  error: any;
  onEdit: (metric: MetricListType | AssessmentPeriodMetricList) => void;
  onDelete: (metric: MetricListType | AssessmentPeriodMetricList) => void;
  tabType: TabType;
  valuesFilterConfig?: ValuesFilterConfig;
  definitionsFilterConfig?: DefinitionsFilterConfig;
}

// Approximate row height in pixels - be conservative to avoid scrolling
const APPROXIMATE_ROW_HEIGHT = 55; // Increased to be more conservative
const TABLE_HEADER_HEIGHT = 50;
const PAGINATION_HEIGHT = 45;
const SAFETY_MARGIN = 20; // Extra margin to prevent scrolling

// Filter popup styles
const filterFieldStyle: React.CSSProperties = {
  marginBottom: '12px',
};

const filterLabelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '6px',
  fontSize: '12px',
  fontWeight: 600,
  color: '#333',
};

const filterDropdownStyle: React.CSSProperties = {
  width: '100%',
};

export const MetricList: React.FC<MetricListProps> = ({
  metrics: metricsProp,
  isLoading,
  error,
  onEdit,
  onDelete,
  tabType,
  valuesFilterConfig,
  definitionsFilterConfig,
}) => {
  // Ensure metrics is always an array - memoize to avoid dependency issues
  const metrics = useMemo(() => Array.isArray(metricsProp) ? metricsProp : [], [metricsProp]);

  // Pagination state
  const [activePage, setActivePage] = useState(1);
  const [containerHeight, setContainerHeight] = useState(0);
  const [isFilterPopupOpen, setIsFilterPopupOpen] = useState(false);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    if (tabType === 'definitions' && definitionsFilterConfig) {
      let count = 0;
      if (definitionsFilterConfig.metricTypeValue !== undefined) count++;
      if (definitionsFilterConfig.statusValue !== undefined) count++;
      return count;
    }
    if (tabType === 'values' && valuesFilterConfig) {
      let count = 0;
      if (valuesFilterConfig.organizationValue !== undefined) count++;
      if (valuesFilterConfig.assessmentPeriodValue !== undefined) count++;
      if (valuesFilterConfig.teamValue !== undefined) count++;
      if (valuesFilterConfig.metricValue !== undefined) count++;
      return count;
    }
    return 0;
  }, [tabType, definitionsFilterConfig, valuesFilterConfig]);
  
  // Calculate items per page based on available space - be conservative
  const itemsPerPage = useMemo(() => {
    if (containerHeight === 0) return 8; // Default fallback
    const availableHeight = containerHeight - TABLE_HEADER_HEIGHT - PAGINATION_HEIGHT - SAFETY_MARGIN;
    const calculatedItems = Math.floor(availableHeight / APPROXIMATE_ROW_HEIGHT);
    // Subtract 1 to be extra conservative and prevent scrolling
    return Math.max(1, calculatedItems - 1);
  }, [containerHeight]);
  
  // Calculate pagination
  const totalPages = Math.ceil(metrics.length / itemsPerPage);
  const paginatedMetrics = useMemo(() => {
    const startIndex = (activePage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return metrics.slice(startIndex, endIndex);
  }, [metrics, activePage, itemsPerPage]);
  
  // Reset to page 1 when metrics change
  React.useEffect(() => {
    setActivePage(1);
  }, [metrics.length]);
  
  // Measure container height
  const containerRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const updateHeight = () => {
      if (containerRef.current) {
        setContainerHeight(containerRef.current.clientHeight);
      }
    };
    
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);
  
  const getMetricTypeColor = (type: number): SemanticCOLORS => {
    switch (type) {
      case MetricType.NPS:
        return 'blue';
      case MetricType.CSAT:
        return 'green';
      case MetricType.CUSTOM_KPI:
        return 'orange';
      case MetricType.GENERIC:
        return 'purple';
      case MetricType.PILLAR:
        return 'teal';
      case MetricType.OUTCOME:
        return 'violet';
      default:
        return 'grey';
    }
  };

  if (isLoading) {
    return (
      <PageSection>
        <LoadingSpinner />
      </PageSection>
    );
  }

  if (error) {
    return (
      <PageSection>
        <div style={{ padding: '24px', color: 'red' }}>
          <h3>Error loading performance metrics</h3>
          <p>{error?.message || 'An error occurred while loading metrics.'}</p>
        </div>
      </PageSection>
    );
  }

  // Empty state message (shown inside the table)
  const emptyMessage = tabType === 'values' && !valuesFilterConfig?.organizationValue
    ? 'Select an organization to view metric values'
    : `No ${tabType === 'definitions' ? 'metric definitions' : 'period metric values'} found`;

  return (
    <PageSection style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 260px)', maxHeight: 'calc(100vh - 260px)' }}>
      <div ref={containerRef} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
          <Table>
            <TableHeader>
              <TableRow>
                {tabType === 'definitions' ? (
                  <>
                    <HeaderCell width={2}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4px' }}>
                        Type
                        <Popup
                          trigger={<Icon name="filter" size="small" style={{ cursor: 'pointer', color: definitionsFilterConfig?.metricTypeValue !== undefined ? '#2185d0' : '#999' }} />}
                          content={
                            <div style={{ minWidth: '220px', padding: '8px' }}>
                              <Dropdown
                                placeholder="All Types"
                                selection
                                fluid
                                options={definitionsFilterConfig?.metricTypeOptions || []}
                                value={definitionsFilterConfig?.metricTypeValue}
                                onChange={(e, { value }) => definitionsFilterConfig?.onMetricTypeChange(value as number | undefined)}
                              />
                            </div>
                          }
                          on="click"
                          position="bottom center"
                        />
                      </div>
                    </HeaderCell>
                    <HeaderCell width={3}>Metric Name</HeaderCell>
                    <HeaderCell width={2}>Label</HeaderCell>
                    <HeaderCell width={1} justify="center">
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                        Active
                        <Popup
                          trigger={<Icon name="filter" size="small" style={{ cursor: 'pointer', color: definitionsFilterConfig?.statusValue !== undefined ? '#2185d0' : '#999' }} />}
                          content={
                            <div style={{ minWidth: '220px', padding: '8px' }}>
                              <Dropdown
                                placeholder="All"
                                selection
                                fluid
                                options={definitionsFilterConfig?.statusOptions || []}
                                value={definitionsFilterConfig?.statusValue}
                                onChange={(e, { value }) => definitionsFilterConfig?.onStatusChange(value as boolean | undefined)}
                              />
                            </div>
                          }
                          on="click"
                          position="bottom center"
                        />
                      </div>
                    </HeaderCell>
                    <HeaderCell width={2} justify="center">Actions</HeaderCell>
                  </>
                ) : (
                  <>
                    <HeaderCell width={2}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4px' }}>
                        Organization
                        <Popup
                          trigger={<Icon name="filter" size="small" style={{ cursor: 'pointer', color: valuesFilterConfig?.organizationValue !== undefined ? '#2185d0' : '#999' }} />}
                          content={
                            <div style={{ minWidth: '220px', padding: '8px' }}>
                              <Dropdown
                                placeholder="Select Organization..."
                                selection
                                search
                                fluid
                                options={valuesFilterConfig?.organizationOptions || []}
                                value={valuesFilterConfig?.organizationValue}
                                onChange={(e, { value }) => valuesFilterConfig?.onOrganizationChange(value as number | undefined)}
                              />
                            </div>
                          }
                          on="click"
                          position="bottom center"
                        />
                      </div>
                    </HeaderCell>
                    <HeaderCell width={2}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4px' }}>
                        Assessment
                        <Popup
                          trigger={<Icon name="filter" size="small" style={{ cursor: 'pointer', color: valuesFilterConfig?.assessmentPeriodValue !== undefined ? '#2185d0' : '#999', opacity: !valuesFilterConfig?.organizationValue ? 0.5 : 1 }} />}
                          content={
                            <div style={{ minWidth: '220px', padding: '8px' }}>
                              <Dropdown
                                placeholder={valuesFilterConfig?.organizationValue ? "All Periods" : "Select Org first"}
                                selection
                                fluid
                                disabled={!valuesFilterConfig?.organizationValue}
                                options={valuesFilterConfig?.assessmentPeriodOptions || []}
                                value={valuesFilterConfig?.assessmentPeriodValue}
                                onChange={(e, { value }) => valuesFilterConfig?.onAssessmentPeriodChange(value as number | undefined)}
                              />
                            </div>
                          }
                          on="click"
                          position="bottom center"
                        />
                      </div>
                    </HeaderCell>
                    <HeaderCell width={2}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4px' }}>
                        Team
                        <Popup
                          trigger={<Icon name="filter" size="small" style={{ cursor: 'pointer', color: valuesFilterConfig?.teamValue !== undefined ? '#2185d0' : '#999', opacity: !valuesFilterConfig?.organizationValue ? 0.5 : 1 }} />}
                          content={
                            <div style={{ minWidth: '220px', padding: '8px' }}>
                              <Dropdown
                                placeholder={valuesFilterConfig?.organizationValue ? "All Teams" : "Select Org first"}
                                selection
                                fluid
                                disabled={!valuesFilterConfig?.organizationValue}
                                options={valuesFilterConfig?.teamOptions || []}
                                value={valuesFilterConfig?.teamValue === null ? 'org' : (valuesFilterConfig?.teamValue || undefined)}
                                onChange={(e, { value }) => {
                                  if (value === 'org') {
                                    valuesFilterConfig?.onTeamChange(null);
                                  } else if (value === null || value === undefined) {
                                    valuesFilterConfig?.onTeamChange(undefined);
                                  } else {
                                    valuesFilterConfig?.onTeamChange(value as number);
                                  }
                                }}
                              />
                            </div>
                          }
                          on="click"
                          position="bottom center"
                        />
                      </div>
                    </HeaderCell>
                    <HeaderCell width={2}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4px' }}>
                        Metric
                        <Popup
                          trigger={<Icon name="filter" size="small" style={{ cursor: 'pointer', color: valuesFilterConfig?.metricValue !== undefined ? '#2185d0' : '#999', opacity: !valuesFilterConfig?.organizationValue ? 0.5 : 1 }} />}
                          content={
                            <div style={{ minWidth: '220px', padding: '8px' }}>
                              <Dropdown
                                placeholder={valuesFilterConfig?.organizationValue ? "All Metrics" : "Select Org first"}
                                selection
                                search
                                fluid
                                disabled={!valuesFilterConfig?.organizationValue}
                                options={valuesFilterConfig?.metricOptions || []}
                                value={valuesFilterConfig?.metricValue}
                                onChange={(e, { value }) => valuesFilterConfig?.onMetricChange(value as number | undefined)}
                              />
                            </div>
                          }
                          on="click"
                          position="bottom center"
                        />
                      </div>
                    </HeaderCell>
                    <HeaderCell width={2} justify="center">Value</HeaderCell>
                    <HeaderCell width={2} justify="center">Target</HeaderCell>
                    <HeaderCell width={2}>Source</HeaderCell>
                    <HeaderCell width={2} justify="center">Actions</HeaderCell>
                  </>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {metrics.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={tabType === 'definitions' ? 5 : 8} style={{ textAlign: 'center', padding: '40px 20px', color: '#666' }}>
                    <div>
                      <h4 style={{ margin: '0 0 8px 0' }}>{emptyMessage}</h4>
                      {tabType === 'values' && !valuesFilterConfig?.organizationValue ? (
                        <p style={{ margin: 0, fontSize: '13px' }}>Use the Organization filter above to get started.</p>
                      ) : (
                        <p style={{ margin: 0, fontSize: '13px' }}>
                          {tabType === 'definitions'
                            ? 'Create your first metric definition to get started.'
                            : 'Try adjusting your filters or create a new metric value.'}
                        </p>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ) : paginatedMetrics.map(metric => {
                if (tabType === 'definitions') {
                  const defMetric = metric as MetricListType;
                  return (
                    <TableRow key={defMetric.id} $selectable>
                      <TableCell $firstcol>
                        <Label color={getMetricTypeColor(defMetric.metric_type)}>
                          {defMetric.metric_type_display || getMetricTypeDisplay(defMetric.metric_type)}
                        </Label>
                      </TableCell>
                      <TableCell>
                        <strong>{defMetric.metric_name}</strong>
                      </TableCell>
                      <TableCell>
                        {defMetric.metric_label || <span style={{ color: '#999' }}>—</span>}
                      </TableCell>
                      <TableCell justify="center">
                        <Label color={defMetric.is_active ? 'green' : 'grey'} size="tiny">
                          {defMetric.is_active ? 'Yes' : 'No'}
                        </Label>
                      </TableCell>
                      <TableCell justify="center">
                        <Dropdown
                          button
                          icon="ellipsis vertical"
                          floating
                          className="icon"
                          direction="left"
                        >
                          <Dropdown.Menu>
                            <Dropdown.Item
                              text="Edit"
                              icon="edit"
                              onClick={() => onEdit(defMetric)}
                            />
                            <Dropdown.Item
                              text="Delete"
                              icon="trash"
                              onClick={() => onDelete(defMetric)}
                            />
                          </Dropdown.Menu>
                        </Dropdown>
                      </TableCell>
                    </TableRow>
                  );
                } else {
                  const periodMetric = metric as AssessmentPeriodMetricList;
                  return (
                    <TableRow key={periodMetric.id} $selectable>
                      <TableCell $firstcol>
                        <Label color="blue" size="tiny">
                          {periodMetric.organization_name || '—'}
                        </Label>
                      </TableCell>
                      <TableCell>
                        {periodMetric.assessment_period_name || `Period ${periodMetric.assessment_period}`}
                      </TableCell>
                      <TableCell>
                        {periodMetric.team_name ? (
                          <Label color="green" size="tiny">{periodMetric.team_name}</Label>
                        ) : (
                          <span style={{ color: '#999' }}>Org Level</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div>
                          <strong>{periodMetric.metric_name || `Metric ${periodMetric.metric}`}</strong>
                          {periodMetric.metric_label && periodMetric.metric_label !== periodMetric.metric_name && (
                            <div style={{ fontSize: '0.85em', color: '#666', marginTop: '2px' }}>
                              {periodMetric.metric_label}
                            </div>
                          )}
                          <div style={{ fontSize: '0.75em', color: '#888', marginTop: '2px' }}>
                            <Label color={getMetricTypeColor(periodMetric.metric_type || 0)} size="mini">
                              {periodMetric.metric_type_display || '—'}
                            </Label>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell justify="center">
                        <strong>{Number(periodMetric.value).toFixed(2)}</strong>
                      </TableCell>
                      <TableCell justify="center">
                        {periodMetric.target_value !== null && periodMetric.target_value !== undefined ? (
                          <span>{Number(periodMetric.target_value).toFixed(2)}</span>
                        ) : (
                          <span style={{ color: '#999' }}>—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {periodMetric.data_source || <span style={{ color: '#999' }}>—</span>}
                      </TableCell>
                      <TableCell justify="center">
                        <Dropdown
                          button
                          icon="ellipsis vertical"
                          floating
                          className="icon"
                          direction="left"
                        >
                          <Dropdown.Menu>
                            <Dropdown.Item
                              text="Edit"
                              icon="edit"
                              onClick={() => onEdit(periodMetric)}
                            />
                            <Dropdown.Item
                              text="Delete"
                              icon="trash"
                              onClick={() => onDelete(periodMetric)}
                            />
                          </Dropdown.Menu>
                        </Dropdown>
                      </TableCell>
                    </TableRow>
                  );
                }
              })}
            </TableBody>
          </Table>
        </div>
        {totalPages > 1 && (
          <div style={{ marginTop: '0', paddingTop: '8px', display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
            <Pagination
              activePage={activePage}
              totalPages={totalPages}
              onPageChange={(e, { activePage }) => {
                setActivePage(activePage as number);
              }}
              firstItem={null}
              lastItem={null}
              prevItem={{ content: '← Previous', icon: true }}
              nextItem={{ content: 'Next →', icon: true }}
            />
          </div>
        )}
      </div>
    </PageSection>
  );
};

